import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import type { RoofTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeNormal } from "geom/plane";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateTopChords,
  generateScissorWebChords,
  projectChordsToFramingPlane,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class ScissorTrussGenerator implements TrussGenerator {
  generate(input: RoofTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      leftEaveFasciaSize,
      rightSpan,
      rightEaveFasciaSize,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      webChordProfile,
      bottomChordOffset,
      trussPlane,
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("Scissor truss does not support multiple spans");
    }

    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);

    const { left, right } = generateTopChords(leftSpan, rightSpan, leftTopChordPitch, rightTopChordPitch);

    const { leftWebChord, rightWebChord, leftHeelChord, rightHeelChord } = generateScissorWebChords(
      leftSpan,
      leftOverhang,
      rightSpan,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordHeight,
      bottomChordHeight,
      bottomChordOffset,
    );

    const [
      leftTopChordTopPointsToFramingPlane,
      rightTopChordTopPointsToFramingPlane,
      leftWebChordToFramingPlane,
      rightWebChordToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ] = projectChordsToFramingPlane(
      [left, right, leftWebChord, rightWebChord, leftHeelChord, rightHeelChord],
      trussPlane,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });
    const [normX, normY, normZ] = planeNormal(trussPlane);

    setupTopChordRails(topChords, leftTopChordTopPointsToFramingPlane, leftEaveFasciaSize, normX, normY, normZ);
    setupTopChordRails(topChords, rightTopChordTopPointsToFramingPlane, rightEaveFasciaSize, normX, normY, normZ);

    [
      leftWebChordToFramingPlane,
      rightWebChordToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ].forEach((webChord) => {
      setupWebChordRails(webChords, webChord, normX, normY, normZ);
    });

    return {
      trussType: RoofTrussType.SCISSOR_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
}
